// src/components/fields/AssetField.jsx
import { useState, useEffect } from 'react';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

export default function AssetField({ field, value, onChange, companyId, readOnly = false, previewMode = false }) {
  const [assets, setAssets] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState(value || []);
  const [selectedFolder, setSelectedFolder] = useState('all'); // 'all', null (unfiled), or folder id
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalUrl, setImageModalUrl] = useState('');
  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [fileModalUrl, setFileModalUrl] = useState('');
  const [fileModalName, setFileModalName] = useState('');

  // Load assets
  useEffect(() => {
    // Skip loading in preview mode
    if (previewMode) {
      setLoading(false);
      return;
    }

    if (!companyId) {
      console.log('AssetField: No companyId provided');
      setLoading(false);
      return;
    }

    console.log('AssetField: Loading assets for company:', companyId);

    const q = query(
      collection(db, 'companies', companyId, 'assets')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('AssetField: Received', snapshot.docs.length, 'assets');
      
      const assetList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by title client-side
      assetList.sort((a, b) => {
        const titleA = (a.title || '').toLowerCase();
        const titleB = (b.title || '').toLowerCase();
        return titleA.localeCompare(titleB);
      });
      
      setAssets(assetList);
      setLoading(false);
    }, (error) => {
      console.error('Error loading assets:', error);
      alert('Failed to load assets: ' + error.message);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [companyId]);

  // Load folders
  useEffect(() => {
    if (previewMode || !companyId) return;

    const q = query(collection(db, 'companies', companyId, 'assetFolders'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const folderList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by createdAt
      folderList.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return aTime - bTime;
      });
      
      setFolders(folderList);
    });

    return () => unsubscribe();
  }, [companyId]);

  // Sync value prop with local state
  useEffect(() => {
    setSelectedAssets(value || []);
  }, [value]);

  function handleToggleAsset(assetId) {
    const asset = assets.find(a => a.id === assetId);
    if (!asset) return;

    let newSelection;
    const isSelected = selectedAssets.some(a => a.id === assetId);

    if (field.config?.multiSelect) {
      // Multi-select mode
      if (isSelected) {
        newSelection = selectedAssets.filter(a => a.id !== assetId);
      } else {
        newSelection = [...selectedAssets, {
          id: asset.id,
          title: asset.title,
          type: asset.type,
          role: asset.role,
          mainPhotoURL: asset.mainPhotoURL,
          fields: asset.fields
        }];
      }
    } else {
      // Single-select mode
      if (isSelected) {
        newSelection = [];
      } else {
        newSelection = [{
          id: asset.id,
          title: asset.title,
          type: asset.type,
          role: asset.role,
          mainPhotoURL: asset.mainPhotoURL,
          fields: asset.fields
        }];
      }
    }

    setSelectedAssets(newSelection);
  }

  function handleSave() {
    onChange?.(selectedAssets);
    setShowModal(false);
  }

  function handleCancel() {
    setSelectedAssets(value || []);
    setShowModal(false);
    setSelectedFolder('all');
  }

  function handleSelectAll() {
    if (!field.config?.multiSelect) return;
    
    // Select all assets in current folder view that match type filter
    const assetsToSelect = getFilteredAssets().map(asset => ({
      id: asset.id,
      title: asset.title,
      type: asset.type,
      role: asset.role,
      mainPhotoURL: asset.mainPhotoURL,
      fields: asset.fields
    }));
    
    setSelectedAssets(assetsToSelect);
  }

  function handleDeselectAll() {
    setSelectedAssets([]);
  }

  // Filter assets by type and folder
  function getFilteredAssets() {
    let filtered = assets;

    // Filter by selectedAssets config if specified
    if (field.config?.selectedAssets && field.config.selectedAssets.length > 0) {
      filtered = filtered.filter(a => field.config.selectedAssets.includes(a.id));
    } else if (field.config?.filterByType && field.config?.assetType) {
      // Only apply type filter if selectedAssets is NOT configured
      filtered = filtered.filter(a => a.type === field.config.assetType);
    }

    // Filter by folder only if user has selected a specific folder
    if (selectedFolder !== 'all') {
      if (selectedFolder === null) {
        // Show only unfiled assets
        filtered = filtered.filter(a => !a.folderId);
      } else {
        // Show only assets in selected folder
        filtered = filtered.filter(a => a.folderId === selectedFolder);
      }
    }
    // If selectedFolder === 'all', show all assets (no folder filtering)

    return filtered;
  }

  const filteredAssets = getFilteredAssets();

  // Calculate folder counts (only count assets that match selectedAssets config)
  const folderCounts = {};
  let baseAssets = assets;
  
  // First apply selectedAssets filter if configured
  if (field.config?.selectedAssets && field.config.selectedAssets.length > 0) {
    baseAssets = baseAssets.filter(a => field.config.selectedAssets.includes(a.id));
  } else if (field.config?.filterByType && field.config?.assetType) {
    // Only apply type filter if selectedAssets is not configured
    baseAssets = baseAssets.filter(a => a.type === field.config.assetType);
  }
  
  folderCounts.unfiled = baseAssets.filter(a => !a.folderId).length;
  folders.forEach(folder => {
    folderCounts[folder.id] = baseAssets.filter(a => a.folderId === folder.id).length;
  });

  // Preview mode - show simple placeholder
  if (previewMode) {
    return (
      <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
        📦 Asset Selector
        {field.config?.multiSelect && <div className="text-xs mt-1">Multiple selection enabled</div>}
        {field.config?.filterByType && field.config?.assetType && (
          <div className="text-xs mt-1">Filtered by: {field.config.assetType}</div>
        )}
        {field.config?.selectedAssets && field.config.selectedAssets.length > 0 && (
          <div className="text-xs mt-1">{field.config.selectedAssets.length} assets pre-selected</div>
        )}
      </div>
    );
  }

  if (readOnly) {
    return (
      <div className="space-y-2">
        {selectedAssets.length === 0 ? (
          <p className="text-gray-500 text-sm italic">No assets selected</p>
        ) : (
          selectedAssets.map(asset => (
            <div key={asset.id} className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50">
              {asset.mainPhotoURL ? (
                <img
                  src={asset.mainPhotoURL}
                  alt={asset.title}
                  className="w-12 h-12 rounded object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded bg-gray-200 flex items-center justify-center text-2xl">
                  {asset.type === 'Person' ? '👤' : 
                   asset.type === 'Vehicle' ? '🚗' :
                   asset.type === 'Equipment' ? '⚙️' : '📦'}
                </div>
              )}
              <div className="flex-1">
                <div className="font-medium">{asset.title}</div>
                {asset.type && (
                  <div className="text-xs text-gray-600">
                    {asset.type} {asset.role && `• ${asset.role}`}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2">
        <button
          onClick={() => setShowModal(true)}
          className="w-full px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-rose-500 dark:hover:border-rose-500 transition-colors text-gray-600 dark:text-gray-400 hover:text-rose-700 dark:hover:text-rose-400"
        >
          {selectedAssets.length === 0 ? (
            <span>
              📦 Select {field.config?.multiSelect ? 'Assets' : 'Asset'}
              {field.config?.filterByType && field.config?.assetType && ` (${field.config.assetType})`}
            </span>
          ) : (
            <span>
              {selectedAssets.length} asset{selectedAssets.length !== 1 ? 's' : ''} selected - Click to change
            </span>
          )}
        </button>

        {/* Selected assets preview */}
        {selectedAssets.length > 0 && (
          <div className="space-y-2">
            {selectedAssets.map(asset => (
              <div key={asset.id} className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
                {asset.mainPhotoURL ? (
                  <img
                    src={asset.mainPhotoURL}
                    alt={asset.title}
                    className="w-12 h-12 rounded object-cover cursor-pointer hover:opacity-75 transition-opacity"
                    onClick={() => {
                      setImageModalUrl(asset.mainPhotoURL);
                      setImageModalOpen(true);
                    }}
                  />
                ) : (
                  <div className="w-12 h-12 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-2xl">
                    {asset.type === 'Person' ? '👤' : 
                     asset.type === 'Vehicle' ? '🚗' :
                     asset.type === 'Equipment' ? '⚙️' : '📦'}
                  </div>
                )}
                <div className="flex-1">
                  <div className="font-medium dark:text-white">{asset.title}</div>
                  {asset.type && (
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      {asset.type} {asset.role && `• ${asset.role}`}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    const newSelection = selectedAssets.filter(a => a.id !== asset.id);
                    setSelectedAssets(newSelection);
                    onChange?.(newSelection);
                  }}
                  className="text-red-600 hover:text-red-700 text-sm"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Asset Selection Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="border-b p-6 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold dark:text-white">
                    Select {field.config?.multiSelect ? 'Assets' : 'Asset'}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {field.config?.multiSelect 
                      ? 'Choose one or more assets' 
                      : 'Choose an asset'}
                    {field.config?.filterByType && field.config?.assetType && 
                      ` (showing only ${field.config.assetType})`}
                  </p>
                </div>
                {/* Action buttons */}
                {field.config?.multiSelect && (
                  <div className="flex gap-2">
                    <button
                      onClick={handleSelectAll}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded"
                    >
                      Select All
                    </button>
                    <button
                      onClick={handleDeselectAll}
                      className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded"
                    >
                      Deselect All
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Content with sidebar */}
            <div className="flex-1 overflow-hidden flex">
              {/* Folder sidebar */}
              <div className="w-56 border-r dark:border-gray-700 p-4 overflow-y-auto bg-gray-50 dark:bg-gray-900">
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Folders</h3>
                
                {/* All Assets */}
                <button
                  onClick={() => setSelectedFolder('all')}
                  className={`w-full flex items-center justify-between p-2 rounded mb-1 text-sm ${
                    selectedFolder === 'all'
                      ? 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span>📋</span>
                    <span>All Assets</span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {baseAssets.length}
                  </span>
                </button>

                {/* Unfiled */}
                <button
                  onClick={() => setSelectedFolder(null)}
                  className={`w-full flex items-center justify-between p-2 rounded mb-1 text-sm ${
                    selectedFolder === null
                      ? 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span>📦</span>
                    <span>Unfiled</span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {folderCounts.unfiled || 0}
                  </span>
                </button>

                {/* Folders */}
                {folders.map(folder => (
                  <button
                    key={folder.id}
                    onClick={() => setSelectedFolder(folder.id)}
                    className={`w-full flex items-center justify-between p-2 rounded mb-1 text-sm ${
                      selectedFolder === folder.id
                        ? 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 truncate">
                      <span>📁</span>
                      <span className="truncate">{folder.name}</span>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                      {folderCounts[folder.id] || 0}
                    </span>
                  </button>
                ))}
              </div>

              {/* Asset grid */}
              <div className="flex-1 p-6 overflow-y-auto">
              {loading ? (
                <div className="text-center py-12 text-gray-500">Loading assets...</div>
              ) : filteredAssets.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">📦</div>
                  <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    {baseAssets.length === 0 ? (
                      'No assets available'
                    ) : selectedFolder === 'all' ? (
                      'No assets found'
                    ) : selectedFolder === null ? (
                      'No assets in Unfiled'
                    ) : (
                      `No assets in ${folders.find(f => f.id === selectedFolder)?.name || 'this folder'}`
                    )}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {baseAssets.length === 0 ? (
                      field.config?.selectedAssets && field.config.selectedAssets.length > 0 
                        ? 'No assets selected in Form Builder properties panel'
                        : 'Create assets in Asset Manager first'
                    ) : (
                      selectedFolder === 'all' ? 'Check your filters' : 'Try selecting a different folder'
                    )}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredAssets.map(asset => {
                    const isSelected = selectedAssets.some(a => a.id === asset.id);
                    
                    return (
                      <button
                        key={asset.id}
                        onClick={() => handleToggleAsset(asset.id)}
                        className={`text-left border-2 rounded-lg p-4 transition-all ${
                          isSelected
                            ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        {/* Photo */}
                        <div className="relative h-32 bg-gray-100 dark:bg-gray-700 rounded mb-3 overflow-hidden">
                          {asset.mainPhotoURL ? (
                            <img
                              src={asset.mainPhotoURL}
                              alt={asset.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-4xl">
                              {asset.type === 'Person' ? '👤' : 
                               asset.type === 'Vehicle' ? '🚗' :
                               asset.type === 'Equipment' ? '⚙️' : '📦'}
                            </div>
                          )}
                          {isSelected && (
                            <div className="absolute top-2 right-2 bg-rose-500 text-white rounded-full w-6 h-6 flex items-center justify-center">
                              ✓
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <h3 className="font-bold text-gray-900 dark:text-white truncate">
                          {asset.title}
                        </h3>
                        {asset.type && (
                          <div className="inline-block bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs px-2 py-0.5 rounded mt-1">
                            {asset.type}
                          </div>
                        )}
                        {asset.role && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 truncate">
                            {asset.role}
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t p-6 dark:border-gray-700 flex justify-between items-center">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {selectedAssets.length} selected
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="bg-rose-700 hover:bg-rose-800 text-white px-4 py-2 rounded-md"
                >
                  Save Selection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      {imageModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={() => setImageModalOpen(false)}>
          <div className="relative max-w-4xl max-h-[90vh] p-4">
            <button
              onClick={() => setImageModalOpen(false)}
              className="absolute top-2 right-2 bg-white dark:bg-gray-800 rounded-full w-10 h-10 flex items-center justify-center text-2xl hover:bg-gray-100 dark:hover:bg-gray-700 z-10"
            >
              ✕
            </button>
            <img
              src={imageModalUrl}
              alt="Asset preview"
              className="max-w-full max-h-[85vh] object-contain rounded"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* File Viewer Modal */}
      {fileModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setFileModalOpen(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold dark:text-white">📎 {fileModalName}</h2>
                <button
                  onClick={() => setFileModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl"
                >
                  ✕
                </button>
              </div>
              <div className="space-y-3">
                <a
                  href={fileModalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-center"
                >
                  🔗 Open in New Tab
                </a>
                <a
                  href={fileModalUrl}
                  download
                  className="block w-full bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white px-4 py-2 rounded text-center"
                >
                  💾 Download File
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
